import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AppShell } from "./components/AppShell.js";
import { hasRole, useAuth, type MembershipState } from "./lib/auth.js";
import { awaitsConfirmationFrom, fetchMyMatches } from "./lib/matches.js";
import { rememberReturnTo, takeReturnTo } from "./lib/return-to.js";
import { Admin } from "./routes/Admin.js";
import { EventManage } from "./routes/EventManage.js";
import { PoolLotsManagePage, PoolSeedingPage, PoolSquadsPage } from "./routes/PoolPages.js";
import { EventBoard } from "./routes/event/EventBoard.js";
import { EventDraw } from "./routes/event/EventDraw.js";
import { EventEntries } from "./routes/event/EventEntries.js";
import { EventFixture } from "./routes/event/EventFixture.js";
import { EventPayments } from "./routes/event/EventPayments.js";
import { EventSeeding } from "./routes/event/EventSeeding.js";
import { EventHub } from "./routes/event/EventHub.js";
import { EventRegister } from "./routes/event/EventRegister.js";
import { EventResults } from "./routes/event/EventResults.js";
import { EventScore } from "./routes/event/EventScore.js";
import { EventStandings } from "./routes/event/EventStandings.js";
import { JoinRequest } from "./routes/JoinRequest.js";
import { Leaderboard } from "./routes/Leaderboard.js";
import { LogMatch } from "./routes/LogMatch.js";
import { Matches } from "./routes/Matches.js";
import { Me } from "./routes/Me.js";
import { Player } from "./routes/Player.js";
import { Privacy } from "./routes/Privacy.js";
import { RatingSystem } from "./routes/RatingSystem.js";
import { SignIn } from "./routes/SignIn.js";
import { NewTournament, TournamentManage } from "./routes/TournamentManage.js";
import { SeedingRank } from "./routes/Seeding.js";
import { PoolLotsPage, TournamentLots } from "./routes/TournamentLots.js";
import { TournamentRules } from "./routes/TournamentRules.js";
import { TournamentAuction } from "./routes/TournamentAuction.js";
import { TournamentAuctionPool } from "./routes/TournamentAuctionPool.js";
import { AuctionBoard } from "./routes/AuctionBoard.js";
import { AuctionCaptain } from "./routes/AuctionCaptain.js";
import { TournamentSeeding } from "./routes/TournamentSeeding.js";
import { TournamentTeams } from "./routes/TournamentTeams.js";
import { FranchisePage } from "./routes/Franchises.js";
import { TournamentPage } from "./routes/TournamentPage.js";
import { PastTournaments } from "./routes/PastTournaments.js";
import { Tournaments } from "./routes/Tournaments.js";
import { Waiting } from "./routes/Waiting.js";

/**
 * The membership gate. Every state in requirements §4 lands on exactly one screen, and
 * the app proper is only reachable from `active`.
 */
export function App() {
  const { state, member } = useAuth();
  const location = useLocation();

  useReturnTo(state, location.pathname + location.search);

  const { data: matches = [] } = useQuery({
    queryKey: ["my-matches", member?.id],
    queryFn: () => fetchMyMatches(member!.id),
    enabled: Boolean(member) && (state === "active" || state === "demo"),
  });

  // Before the gate, and deliberately not a <Route>. The gate short-circuits every state
  // except `active` by returning a screen instead of routing, so a signed-out visitor never
  // reaches the router at all. It has to render in every state, including while the session
  // is still loading.
  if (location.pathname === "/privacy") return <Privacy />;
  if (location.pathname === "/rating") return <RatingSystem />;

  if (state === "loading") {
    return (
      <main className="mx-auto max-w-md px-4 pt-16">
        <p className="micro">Loading…</p>
      </main>
    );
  }

  if (state === "signed-out") return <SignIn />;
  if (state === "no-member") return <JoinRequest />;
  if (state !== "active" && state !== "demo") return <Waiting state={state} />;

  const awaiting = member
    ? matches.filter((match) => awaitsConfirmationFrom(match, member.id)).length
    : 0;

  return (
    <Routes>
      {/* Outside the shell on purpose: the court board goes on a television, and a
          television has no use for a bottom tab bar. */}
      <Route path="/events/:eventId/board" element={<EventBoard />} />
      <Route path="/tournaments/:tournamentId/auction/board" element={<AuctionBoard />} />

      <Route element={<AppShell awaitingCount={awaiting} />}>
        {/* The events are the front door: nearly everybody opening the app is here to enter
            something or see when they play. The board is a tab away. */}
        <Route path="/" element={<Navigate to="/events" replace />} />
        <Route path="/board" element={<Leaderboard />} />
        <Route path="/log" element={<LogMatch />} />
        <Route path="/events" element={<Tournaments />} />
        {/* One event, one screen per reason to open it. Registering, reading the draw,
            entering a score and reading the table are four different errands, and each is
            now a link somebody can send. */}
        <Route path="/events/:eventId" element={<EventHub />} />
        <Route path="/events/:eventId/register" element={<EventRegister />} />
        <Route path="/events/:eventId/entries" element={<EventEntries />} />
        <Route path="/events/:eventId/draw" element={<EventDraw />} />
        <Route path="/events/:eventId/score/:fixtureId" element={<EventScore />} />
        {/* Organiser only, like /manage: changing who is drawn to play, or taking the match
            out altogether. Its own screen because it is reached from one line of the draw
            with somebody's ankle already swelling. */}
        <Route path="/events/:eventId/fixture/:fixtureId" element={<EventFixture />} />
        <Route path="/events/:eventId/results" element={<EventResults />} />
        <Route path="/events/:eventId/standings" element={<EventStandings />} />
        {/* Organiser only, and `EventManage` sends anybody else back to the event. The
            database refuses every write on it regardless, so this is signposting rather
            than security. */}
        <Route path="/events/:eventId/manage" element={<EventManage />} />
        {/* Organiser only, like /manage: one auction pool's share of the set-up, a page per
            step. Under the event because the tournament's URLs with an event id are the
            panelist's and the captain's. */}
        <Route path="/events/:eventId/manage/squads" element={<PoolSquadsPage />} />
        <Route path="/events/:eventId/manage/seeding" element={<PoolSeedingPage />} />
        <Route path="/events/:eventId/manage/lots" element={<PoolLotsManagePage />} />
        {/* Organiser only, and sends anybody else back to the event: who has paid the
            registration amount. The database answers nobody else either. */}
        <Route path="/events/:eventId/payments" element={<EventPayments />} />
        {/* Organiser only, the same way: the order the next draw is seeded in. */}
        <Route path="/events/:eventId/seeding" element={<EventSeeding />} />
        {/* Organiser only, like /events/:id/manage; both send anybody else back. "new"
            comes before the :tournamentId route so it isn't read as an id. */}
        <Route path="/tournaments/new" element={<NewTournament />} />
        {/* Organisers only, and sends anybody else back to Events. Static, so it is declared
            before :tournamentId and is not read as one. */}
        <Route path="/tournaments/past" element={<PastTournaments />} />
        <Route path="/tournaments/:tournamentId/manage" element={<TournamentManage />} />
        <Route path="/tournaments/:tournamentId/teams" element={<TournamentTeams />} />
        <Route
          path="/tournaments/:tournamentId/franchises/:franchiseId"
          element={<FranchisePage />}
        />
        <Route path="/tournaments/:tournamentId/lots" element={<TournamentLots />} />
        <Route path="/tournaments/:tournamentId/lots/:eventId" element={<PoolLotsPage />} />
        <Route path="/tournaments/:tournamentId/rules" element={<TournamentRules />} />
        <Route path="/tournaments/:tournamentId/auction" element={<TournamentAuction />} />
        {/* A captain's phone during the auction: follow the pool they captain, raise for their
            team. Anybody else is told there is nothing here; the database sends them nothing. */}
        <Route path="/tournaments/:tournamentId/bid" element={<AuctionCaptain />} />
        <Route
          path="/tournaments/:tournamentId/auction/:eventId"
          element={<TournamentAuctionPool />}
        />
        <Route path="/tournaments/:tournamentId/seeding" element={<TournamentSeeding />} />
        <Route path="/tournaments/:tournamentId/seeding/:eventId" element={<SeedingRank />} />
        <Route path="/tournaments/:tournamentId" element={<TournamentPage />} />
        <Route path="/matches" element={<Matches />} />
        <Route path="/me" element={<Me />} />
        <Route path="/player/:id" element={<Player />} />
        <Route
          path="/admin"
          element={hasRole(member, "admin") ? <Admin /> : <Navigate to="/me" replace />}
        />
        <Route path="*" element={<Navigate to="/events" replace />} />
      </Route>
    </Routes>
  );
}

/**
 * Carry a deep link across the sign-in round trip.
 *
 * The gate above returns a screen rather than routing, so a signed-out visitor holding a
 * link to an event never reaches the router — and the trip through Google comes back to the
 * origin with the path gone. Remember it while they are outside the gate, spend it the
 * moment they are through.
 *
 * Deliberately not restricted to the sign-in screen. `no-member` and `pending` are outside
 * the gate too, and somebody sent a link to Friday's doubles who has to ask to join and wait
 * for an admin should still land on Friday's doubles when they are let in.
 */
function useReturnTo(state: MembershipState, here: string) {
  const navigate = useNavigate();

  useEffect(() => {
    if (state === "loading") return;

    if (state === "active" || state === "demo") {
      const saved = takeReturnTo();
      // Same path means they got here under their own steam and there is nothing to restore.
      if (saved && saved !== here) navigate(saved, { replace: true });
      return;
    }

    rememberReturnTo(here);
  }, [state, here, navigate]);
}
